// @flow
import React, { useMemo, useState } from "react";
import ScheduleGameCard from "./ScheduleGameCard";
import type {
  Game,
  GameFilterTypes,
  TeamFilterTypes,
} from "../../common/types";
import Box from "@material-ui/core/Box";
import gameSelectors from "../../selectors/gameSelectors";
import ScheduleGameCardEmpty from "./ScheduleGameCardEmpty";
import {
  SuspenseWithPerf,
  useFirestore,
  useFirestoreCollectionData,
} from "reactfire";
import SchedulePageHeading from "./SchedulePageHeading";
import ScheduleGameCardSkeleton from "./ScheduleGameCardSkeleton";
import Button from "@material-ui/core/Button";
import ScheduleCancelHint from "./ScheduleCancelHint";

type Props = {
  gameFilter: GameFilterTypes,
  teamFilter: TeamFilterTypes,
};

function ScheduleGameList({ gameFilter, teamFilter }: Props) {
  const firestore = useFirestore();
  const gamesRef = firestore.collection("games").orderBy("date", "asc");
  const games = useFirestoreCollectionData(gamesRef);

  const [expandGames, setExpandGames] = useState(false);
  const collapsedUpcomingGamesAmount = 8;

  /**
   * apply filters
   */
  const gamesFilteredByTeam = useMemo(
    () => gameSelectors.getGamesFilteredByTeam(games, teamFilter),
    [games, teamFilter]
  );

  const gamesFilteredByLocation = useMemo(
    () => gameSelectors.getGamesFilteredByType(gamesFilteredByTeam, gameFilter),
    [gamesFilteredByTeam, gameFilter]
  );

  const gamesWithTimestamp = useMemo(
    () => gameSelectors.getGamesWithTimestamp(gamesFilteredByLocation),
    [gamesFilteredByLocation]
  );

  const todaysGames = useMemo(
    () => gameSelectors.getTodaysGames(gamesWithTimestamp),
    [gamesWithTimestamp]
  );

  const upcomingGamesSorted = useMemo(
    () => gameSelectors.getUpcomingGames(gamesWithTimestamp),
    [gamesWithTimestamp]
  );

  const pastGamesSorted = useMemo(
    () => gameSelectors.getPastGames(gamesWithTimestamp),
    [gamesWithTimestamp]
  );

  /**
   * create output
   */

  return (
    <>
      <ScheduleCancelHint />
      {todaysGames.length > 0 && (
        <>
          <SchedulePageHeading title="Heute" gameCount={todaysGames.length} />
          {todaysGames.map((game: Game) => (
            <Box key={game.id} mb={2}>
              <SuspenseWithPerf
                traceId="GameCardMatchDay"
                fallback={<ScheduleGameCardSkeleton />}
              >
                <ScheduleGameCard game={game} type="MATCH_DAY" />
              </SuspenseWithPerf>
            </Box>
          ))}
        </>
      )}
      <SchedulePageHeading
        title="Als nächstes"
        gameCount={upcomingGamesSorted.length}
      />
      {upcomingGamesSorted.length > 0 ? (
        upcomingGamesSorted.map((game: Game, index: number) => {
          if (index === collapsedUpcomingGamesAmount && !expandGames) {
            const extraGames =
              upcomingGamesSorted.length - collapsedUpcomingGamesAmount;
            return (
              <div style={{ textAlign: "center" }} key="more-games-button">
                <Button
                  onClick={() => setExpandGames(true)}
                  variant="text"
                  color="primary"
                >
                  {extraGames} weitere Spiele anzeigen
                </Button>
              </div>
            );
          }
          if (index > collapsedUpcomingGamesAmount && !expandGames) {
            return null;
          }
          return (
            <Box key={game.id} mb={2}>
              <SuspenseWithPerf
                traceId="GameCard"
                fallback={<ScheduleGameCardSkeleton />}
              >
                <ScheduleGameCard game={game} type="UPCOMING" />
              </SuspenseWithPerf>
            </Box>
          );
        })
      ) : (
        <ScheduleGameCardEmpty />
      )}
      {pastGamesSorted.length > 0 && (
        <>
          <SchedulePageHeading
            title="Zuletzt"
            gameCount={pastGamesSorted.length}
          />
          {pastGamesSorted.map((game: Game) => (
            <Box key={game.id} mb={1}>
              <SuspenseWithPerf
                traceId="GameCardPast"
                fallback={<ScheduleGameCardSkeleton />}
              >
                <ScheduleGameCard game={game} type="RESULT" />
              </SuspenseWithPerf>
            </Box>
          ))}
        </>
      )}
    </>
  );
}

export default ScheduleGameList;
